import React, { useContext, useEffect, useMemo, useState, } from "react";
import { Link } from "wouter";
import Swal from "sweetalert2";
import { PlusIcon, MagnifyingGlassIcon, TagIcon, TrashIcon, XMarkIcon, PencilSquareIcon, CheckIcon, } from "@heroicons/react/24/outline";
import { AuthContext } from "../contexts/auth";
import PopoverComponentCustom from "../components/popover_component";
import Footer from "../components/footer";
import Fetching from "../components/fetching";
import Empty from "../components/empty";
import { getAreaMarkerList, addAreaMarker, deleteAreaMarker, patchAreaMarker, } from "../services/api_justifications";
import { estraiMessaggiErrore } from "../services/utils";
import PrettyPagination from "./pretty_pagination";
import V2ListBoxPageSize from "./v2_listbox_page_size";


const EMPTY_FORM = { code: "", description: "", };

export default function WorkingAreaMarker() {
    const { profile, token } = useContext(AuthContext);
    const isProfileComplete = profile?.has_complete_profile;

    const [identifiers, setIdentifiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [editErrors, setEditErrors] = useState({});
    const [updating, setUpdating] = useState(false);

    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    const totalPages = Math.ceil(totalCount / pageSize);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 400);

        return () => clearTimeout(timeout);
    }, [search]);

    const fetchIdentifiers = async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await getAreaMarkerList(token, {
                search: debouncedSearch || undefined,
                page,
                page_size: pageSize,
            });

            setIdentifiers(response?.results || []);
            setTotalCount(response?.count || 0);
        } catch (error) {
            console.error("Errore caricamento identificativi", error);

            setIdentifiers([]);
            setTotalCount(0);

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIdentifiers();
    }, [token, page, pageSize, debouncedSearch,]);

    const openForm = () => {
        /*
        * Se sto modificando una riga, chiudo prima
        * la modalità modifica.
        */
        setEditingId(null);
        setEditForm(EMPTY_FORM);
        setEditErrors({});

        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const closeForm = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(false);
    };

    const validateForm = () => {
        const nextErrors = {};

        const code = form.code.trim();
        const description = form.description.trim();

        if (!code) {
            nextErrors.code = "Il codice è obbligatorio";
        } else if (code.length > 3) {
            nextErrors.code = "Il codice può contenere massimo 3 caratteri";
        }

        if (!description) { nextErrors.description = "La descrizione è obbligatoria";}

        const duplicated = identifiers.some((item) =>
            item.code?.trim().toLowerCase() === code.toLowerCase()
        );

        if (duplicated) { nextErrors.code = "Questo codice è già presente";}

        setErrors(nextErrors);

        return (Object.keys(nextErrors).length === 0);
    };

    const handleSave = async () => {
        if (!validateForm()) { return;}

        setSaving(true);
        setErrors({});

        const payload = { code: form.code.trim(), description: form.description.trim(),};

        try {
            const created = await addAreaMarker(token, payload);

            if (page !== 1) {
                setPage(1);
            } else {
                await fetchIdentifiers();
            }

            closeForm();

            Swal.fire({
                icon: "success",
                title: "Identificativo creato",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Errore creazione identificativo", error);

            const responseErrors = error?.response?.data;

            if (responseErrors && typeof responseErrors === "object" && !Array.isArray(responseErrors)) {
                setErrors(responseErrors);
            }

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleStartEdit = (identifier) => {
        closeForm();
        setEditingId(identifier.id);
        setEditForm({ code: identifier.code || "", description: identifier.description || "",});
        setEditErrors({});
    };


    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm(EMPTY_FORM);
        setEditErrors({});
    };

    const validateEditForm = () => {
        const nextErrors = {};

        const code = editForm.code.trim();

        const description = editForm.description.trim();

        if (!code) {nextErrors.code = "Il codice è obbligatorio";
        } else if (code.length > 3) {nextErrors.code = "Il codice può contenere massimo 3 caratteri";}

        if (!description) {nextErrors.description = "La descrizione è obbligatoria";}

        const duplicated = identifiers.some((item) => item.id !== editingId && item.code?.trim().toLowerCase() === code.toLowerCase());

        if (duplicated) {nextErrors.code = "Questo codice è già presente";}
            setEditErrors(nextErrors);
        return (Object.keys(nextErrors).length === 0);
    };

    const handlePatch = async (identifier) => {
        if (!validateEditForm()) {
            return;
        }

        setUpdating(true);
        setEditErrors({});

        const payload = {};

        const newCode = editForm.code.trim();

        const newDescription = editForm.description.trim();

        if (newCode !== identifier.code) {payload.code = newCode;}

        if (newDescription !== identifier.description ) {payload.description = newDescription;}

        if (Object.keys(payload).length === 0) {handleCancelEdit(); return;}

        try {
            const updated = await patchAreaMarker(token, identifier.id, payload);

            setIdentifiers((prev) =>
                prev.map((item) => {
                if (item.id !== identifier.id) {
                    return item;
                }

                if (updated) {
                    return {...item, ...updated,};
                }

                    return {...item, ...payload,};
                })
            );

            handleCancelEdit();

            Swal.fire({
                icon: "success",
                title: "Identificativo aggiornato",
                timer: 1000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Errore modifica identificativo", error);

            const responseErrors = error?.response?.data;

            if (responseErrors && typeof responseErrors === "object" && !Array.isArray(responseErrors)) {
                setEditErrors(responseErrors);
            }

            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async (identifier) => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Confermi eliminazione?",
            text: `Giustificativo: ${identifier.code} - ${identifier.description}`,
            showCancelButton: true,
            confirmButtonText: "Elimina",
            cancelButtonText: "Annulla",
            confirmButtonColor: "#dc2626",
        });

        if (!confirm.isConfirmed) {return;}

        try {
            await deleteAreaMarker(token, identifier.id);

            if (editingId === identifier.id) {
                handleCancelEdit();
            }

            if (identifiers.length === 1 && page > 1) {
                setPage((prev) => prev - 1);
            } else {
                await fetchIdentifiers();
            }

            Swal.fire({
                icon: "success",
                title: "Identificativo eliminato",
                timer: 1000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Errore eliminazione identificativo", error);
            Swal.fire({
                icon: "error",
                title: "Errore",
                text: estraiMessaggiErrore(error),
            });
        }
    };

    return (
        <div className="flex min-h-screen flex-col">
            <PopoverComponentCustom title="Giustificativi Aree di Lavoro" />
            {profile?.customer_type === "company" && !isProfileComplete && profile?.company?.email === profile?.the_real_user_email && (
                <div className="w-full">
                    <div className="mb-6 bg-orange-100 p-4 text-orange-600">
                        <p className="text-center text-sm font-semibold">
                            Il profilo non è completo.<br />Compila i campi mancanti{" "}
                            <Link to="/profilo/fatturazione" className="text-indigo-600 underline hover:text-indigo-800">cliccando qui</Link>
                        </p>
                    </div>
                </div>
            )}
            <main className="flex-grow py-8">
                <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
                    <div className="mb-5">
                        <Link to="/working_area/manage" className="text-sm font-semibold text-indigo-600 hover:underline">← Torna alle Aree di Lavoro</Link>
                    </div>
                    <div className="rounded-xl border border-indigo-100 bg-white shadow-sm ring-1 ring-black/5">
                        <div className="flex flex-col gap-4 px-5 py-4 md:flex-row md:items-center md:justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><TagIcon className="h-6 w-6" /></div>
                                <div>
                                    <h1 className="text-sm font-semibold uppercase text-gray-800">Giustificativo Identificazione Area</h1>
                                    <p className="mt-0.5 text-xs text-gray-500">Crea il codice identificativo da collegare all'area di lavoro. Verrà utilizzato come giustificativo nelle statistiche e Calendario Presenze</p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={openForm}
                                disabled={showForm || updating}
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <PlusIcon className="h-4 w-4" /> AGGIUNGI
                            </button>
                        </div>

                        <div className="border-t border-gray-100" />

                        {/* Ricerca */}
                        <div className="px-5 py-3">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Cerca per codice o descrizione..."
                                    className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-10 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-3 bg-gray-50 px-6 py-2 border-t border-gray-100">
                            <p className="text-xs text-gray-500">
                                <span className="font-semibold text-gray-700">
                                    {identifiers.length}
                                </span>{" "}di{" "}<span className="font-semibold text-gray-700">{totalCount}</span>{" "}giustificativi
                            </p>
                            <V2ListBoxPageSize
                                value={pageSize}
                                onChange={(value) => {
                                    setPageSize(Number(value));
                                    setPage(1);
                                }}
                            />
                        </div>
                        
                    </div>
                    {showForm && (
                        <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/30 p-5">
                            <div className="mb-4 flex items-start justify-between">
                                <div>
                                    <h2 className="text-sm font-semibold text-indigo-800">
                                        Nuovo identificativo
                                    </h2>

                                    <p className="mt-1 text-xs text-gray-500">
                                        L'identificativo sarà disponibile nella configurazione delle aree di lavoro.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeForm}
                                    disabled={saving}
                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-gray-700">Codice<span className="ml-1 text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        maxLength={3}
                                        value={form.code}
                                        disabled={saving}
                                        onChange={(event) =>
                                        setForm((prev) => ({...prev, code: event.target.value.slice(0, 3),}))}
                                        placeholder="Es. MAG"
                                        className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100
                                        ${errors.code ? "border-red-400" : "border-gray-300"}`}
                                    />
                                    {errors.code && (
                                        <p className="mt-1 text-xs text-red-600">{Array.isArray(errors.code) ? errors.code[0] : errors.code}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-xs font-medium text-gray-700">Descrizione<span className="ml-1 text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        maxLength={255}
                                        value={form.description}
                                        disabled={saving}
                                        onChange={(event) => setForm((prev) => ({...prev, description: event.target.value,}))}
                                        placeholder="Es. Area magazzino"
                                        className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100
                                        ${errors.description ? "border-red-400" : "border-gray-300"}`}
                                    />
                                    {errors.description && (
                                        <p className="mt-1 text-xs text-red-600">{Array.isArray(errors.description) ? errors.description[0] : errors.description}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {saving ? "Salvataggio..." : "Crea identificativo"}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeForm}
                                    disabled={saving}
                                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 overflow-hidden rounded-xl border border-gray-300 bg-white">
                        <div className="overflow-x-auto">
                            <table className="table-fixed w-full border-collapse text-sm">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="w-[15%] border-r border-gray-300 px-4 py-2 text-left font-semibold text-gray-600">
                                            Codice
                                        </th>
                                        <th className="w-[65%] border-r border-gray-300 px-4 py-2 text-left font-semibold text-gray-600">
                                            Descrizione
                                        </th>
                                        <th className="w-[20%] px-4 py-2 text-center font-semibold text-gray-600">
                                            Azioni
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="3" className="py-6 text-center">
                                                <Fetching />
                                            </td>
                                        </tr>
                                    ) : identifiers.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-8">
                                                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-gray-200 bg-gray-50 px-6 py-10 text-center">
                                                <TagIcon className="mb-4 h-10 w-10 text-gray-400" />

                                                <h3 className="text-sm font-semibold text-gray-800">
                                                    {search
                                                    ? "Nessun risultato trovato"
                                                    : "Nessun identificativo area disponibile"}
                                                </h3>

                                                <p className="mt-2 max-w-md text-sm text-gray-500">
                                                    {search
                                                    ? "Prova a modificare il testo della ricerca"
                                                    : "Non sono presenti giustificativi configurati. Seleziona \"Aggiungi\" per procedere alla crezione."}
                                                </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        identifiers.map(
                                        (identifier) => {
                                            const isEditing = editingId === identifier.id;

                                            return (
                                            <tr key={identifier.id} className={` border-t border-gray-300 ${isEditing ? "bg-indigo-50/40" : "hover:bg-gray-50"}`}>
                                                <td className="border-r border-gray-300 px-4 py-3 align-top">
                                                    {isEditing ? (
                                                        <div>
                                                            <input
                                                                type="text"
                                                                maxLength={3}
                                                                value={editForm.code}
                                                                disabled={updating}
                                                                onChange={(event) => setEditForm((prev) => ({...prev, code: event.target.value.slice(0, 3),}))}
                                                                className={`w-full rounded-md border px-2 py-1.5 text-xs font-semibold outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100
                                                                ${editErrors.code ? "border-red-400" : "border-gray-300"}`}
                                                            />
                                                            {editErrors.code && (
                                                                <p className="mt-1 text-xs text-red-600">{Array.isArray(editErrors.code) ? editErrors.code[0] : editErrors.code}</p>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex min-w-[45px] justify-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-bold text-indigo-700">
                                                            {identifier.code}
                                                        </span>
                                                    )}
                                                </td>
                                                    <td className="break-words whitespace-normal border-r border-gray-300 px-4 py-3 align-top text-gray-800">
                                                        {isEditing ? (
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    maxLength={255}
                                                                    value={editForm.description}
                                                                    disabled={updating}
                                                                    onChange={(event) => setEditForm((prev) => ({...prev, description: event.target.value, }))}
                                                                    className={`w-full rounded-md border px-3 py-1.5 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100
                                                                    ${editErrors.description ? "border-red-400" : "border-gray-300"}`}
                                                                />
                                                                {editErrors.description && (
                                                                    <p className="mt-1 text-xs text-red-600">
                                                                        {Array.isArray(editErrors.description) ? editErrors.description[0] : editErrors.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            identifier.description
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-3 align-center">
                                                        <div className="flex justify-center gap-3">
                                                            {isEditing ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handlePatch(identifier)}
                                                                        disabled={updating}
                                                                        title="Conferma modifiche"
                                                                        className="uppercase h-8 rounded-full border border-indigo-500 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-40"
                                                                    >
                                                                        {updating ? "Salvataggio..." : "Conferma"}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        onClick={handleCancelEdit}
                                                                        disabled={updating}
                                                                        title="Annulla modifica"
                                                                        className="uppercase h-8 rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                                                    >
                                                                        Annulla
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleStartEdit(identifier)}
                                                                        disabled={updating}
                                                                        title="Modifica"
                                                                        className="text-gray-500 hover:text-indigo-600 disabled:opacity-40"
                                                                    >
                                                                        <PencilSquareIcon className="h-5 w-5" />
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDelete(identifier)}
                                                                        disabled={updating}
                                                                        title="Elimina"
                                                                        className="text-gray-500 hover:text-red-600 disabled:opacity-40"
                                                                    >
                                                                        <TrashIcon className="h-5 w-5" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                            {totalPages && totalPages > 1 ? (
                                <div className="pb-2 border-t border-gray-200">
                                    <PrettyPagination
                                        page={page}
                                        totalPages={totalPages}
                                        totalCount={totalCount}
                                        onChange={(newPage) => {
                                            setPage(newPage);
                                            handleCancelEdit();
                                        }}
                                        labelSingolare="identificativo"
                                        labelPlurale="identificativi"
                                        className="mt-4"
                                    />
                                </div>   
                            ) : ('')}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}